/**
 * Audio Capture Service
 * 
 * Handles browser microphone capture and WebSocket audio transmission
 * to Wave VMS for two-way intercom communication.
 * 
 * Wave VMS Audio API:
 *   wss://<host>:<port>/api/http_audio?camera_Id=<id>&format=f32le&sample_rate=44100&channels=1
 * 
 * We proxy through our backend at:
 *   wss://<our_host>/ws/audio/<cameraId>?tenantId=<tenantId>
 */

class AudioCaptureService {
  constructor() {
    this.stream = null;
    this.audioContext = null;
    this.workletNode = null;
    this.sourceNode = null;
    this.ws = null;
    this.isCapturing = false;
    this.isPTTActive = false;
    this.pttMode = false; // Open mic by default (muted-on-connect, see intercomCallStore)
    this.onAudioLevel = null; // Callback for audio level visualization
    this.onStateChange = null; // Callback for connection state changes
    this.analyserNode = null;
    this._reconnectAttempts = 0;
    this._maxReconnectAttempts = 3;
  }

  /**
   * Request microphone permission and initialize audio pipeline
   * @returns {Promise<boolean>} - Whether mic access was granted
   */
  async requestMicAccess() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1
        }
      });
      console.log('[AudioCapture] Mic access granted');
      return true;
    } catch (err) {
      console.error('[AudioCapture] Mic access denied:', err.message);
      return false;
    }
  }

  /**
   * Initialize the Web Audio pipeline
   * MediaStream -> AudioContext -> AudioWorklet -> WebSocket
   */
  async initAudioPipeline() {
    if (!this.stream) {
      throw new Error('No media stream - call requestMicAccess first');
    }

    // Create AudioContext at 44100Hz to match Wave VMS expected format
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: 44100
    });

    // iOS Safari requires a user gesture to resume AudioContext
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    // Load the AudioWorklet processor
    await this.audioContext.audioWorklet.addModule('/pcm-processor.worklet.js');

    // Create source from mic stream
    this.sourceNode = this.audioContext.createMediaStreamSource(this.stream);

    // Create analyser for audio level visualization
    this.analyserNode = this.audioContext.createAnalyser();
    this.analyserNode.fftSize = 256;
    this.analyserNode.smoothingTimeConstant = 0.8;

    // Create the worklet node
    this.workletNode = new AudioWorkletNode(this.audioContext, 'pcm-processor');

    // Handle audio data from the worklet
    this.workletNode.port.onmessage = (event) => {
      if (event.data.type === 'audio') {
        this._sendAudioData(event.data.buffer);
      }
    };

    // Connect the pipeline: mic -> analyser -> worklet
    this.sourceNode.connect(this.analyserNode);
    this.analyserNode.connect(this.workletNode);
    // Don't connect worklet to destination (we don't want to hear ourselves)

    // Start audio level monitoring
    this._startLevelMonitor();

    console.log('[AudioCapture] Audio pipeline initialized');
  }

  /**
   * Connect WebSocket to Wave VMS audio endpoint via our proxy
   * @param {string} cameraId - Wave VMS camera ID (with or without braces)
   * @param {string} tenantId - Tenant ID for multi-tenant routing
   * @returns {Promise<boolean>} - Whether connection was established
   */
  async connectAudioWebSocket(cameraId, tenantId) {
    return new Promise((resolve, reject) => {
      // Clean camera ID (remove braces if present)
      const cleanId = cameraId.replace(/[{}]/g, '');

      // Build WebSocket URL through our proxy
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${window.location.host}/ws/audio/${cleanId}?tenantId=${tenantId || 'default'}`;

      console.log('[AudioCapture] Connecting audio WebSocket:', wsUrl);
      this._notifyState('connecting');

      this.ws = new WebSocket(wsUrl);
      this.ws.binaryType = 'arraybuffer';

      this.ws.onopen = () => {
        console.log('[AudioCapture] Audio WebSocket connected');
        this._reconnectAttempts = 0;
        this.isCapturing = true;
        this._notifyState('connected');
        resolve(true);
      };

      this.ws.onclose = (event) => {
        console.log('[AudioCapture] Audio WebSocket closed:', event.code);
        this.isCapturing = false;
        this._notifyState('disconnected');
      };

      this.ws.onerror = (error) => {
        console.error('[AudioCapture] Audio WebSocket error:', error);
        this._notifyState('error');
        reject(new Error('Audio WebSocket connection failed'));
      };

      this.ws.onmessage = (event) => {
        // Wave VMS may send audio from the camera mic back through this connection
        // For now we handle incoming audio in the WebRTC video stream
      };

      // Timeout after 10 seconds
      setTimeout(() => {
        if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
          this.ws.close();
          reject(new Error('Audio WebSocket connection timeout'));
        }
      }, 10000);
    });
  }

  /**
   * Send raw PCM audio data to Wave VMS
   * Only sends if PTT is active (in PTT mode) or if in open mic mode
   */
  _sendAudioData(buffer) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (!this.isCapturing) return;

    // In PTT mode, only send when button is held
    if (this.pttMode && !this.isPTTActive) return;

    // Send raw Float32 PCM bytes
    this.ws.send(buffer);
  }

  /**
   * Start push-to-talk (call when PTT button is pressed)
   */
  startPTT() {
    this.isPTTActive = true;
    console.log('[AudioCapture] PTT active - transmitting');
    
    // Haptic feedback on mobile
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  }

  /**
   * Stop push-to-talk (call when PTT button is released)
   */
  stopPTT() {
    this.isPTTActive = false;
    console.log('[AudioCapture] PTT released - stopped transmitting');
  }

  /**
   * Toggle between PTT and open mic mode
   */
  togglePTTMode() {
    this.pttMode = !this.pttMode;
    if (!this.pttMode) {
      this.isPTTActive = true; // Open mic = always transmitting
    } else {
      this.isPTTActive = false; // PTT = only when held
    }
    console.log('[AudioCapture] Mode:', this.pttMode ? 'Push-to-Talk' : 'Open Mic');
    return this.pttMode;
  }

  /**
   * Set PTT mode explicitly
   */
  setPTTMode(enabled) {
    this.pttMode = enabled;
    if (!enabled) {
      this.isPTTActive = true;
    } else {
      this.isPTTActive = false;
    }
  }

  /**
   * Monitor audio input level for UI visualization
   */
  _startLevelMonitor() {
    if (!this.analyserNode) return;

    const dataArray = new Uint8Array(this.analyserNode.frequencyBinCount);

    const checkLevel = () => {
      if (!this.isCapturing) return;

      this.analyserNode.getByteFrequencyData(dataArray);

      // Calculate RMS level (0-1)
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i] * dataArray[i];
      }
      const rms = Math.sqrt(sum / dataArray.length) / 255;

      if (this.onAudioLevel) {
        this.onAudioLevel(rms);
      }

      requestAnimationFrame(checkLevel);
    };

    requestAnimationFrame(checkLevel);
  }

  /**
   * Notify state change to callback
   */
  _notifyState(state) {
    if (this.onStateChange) {
      this.onStateChange(state);
    }
  }

  /**
   * Start a full audio call session
   * @param {string} cameraId - Wave VMS camera ID
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<boolean>} - Success
   */
  async startCall(cameraId, tenantId) {
    try {
      // 1. Request mic access
      const micGranted = await this.requestMicAccess();
      if (!micGranted) {
        throw new Error('Microphone access denied');
      }

      // 2. Initialize audio pipeline
      await this.initAudioPipeline();

      // 3. Connect to Wave VMS audio WebSocket
      await this.connectAudioWebSocket(cameraId, tenantId);

      console.log('[AudioCapture] Call started successfully');
      return true;
    } catch (err) {
      console.error('[AudioCapture] Failed to start call:', err);
      this.cleanup();
      throw err;
    }
  }

  /**
   * End the call and clean up all resources
   */
  cleanup() {
    console.log('[AudioCapture] Cleaning up audio resources');

    this.isCapturing = false;
    this.isPTTActive = false;

    // Stop the worklet
    if (this.workletNode) {
      this.workletNode.port.postMessage({ type: 'stop' });
      this.workletNode.disconnect();
      this.workletNode = null;
    }

    // Disconnect analyser
    if (this.analyserNode) {
      this.analyserNode.disconnect();
      this.analyserNode = null;
    }

    // Disconnect source
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    // Close AudioContext
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }

    // Stop all media tracks (releases mic)
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    // Close WebSocket
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this._notifyState('idle');
  }
}

// Singleton instance
const audioCaptureService = new AudioCaptureService();

export default audioCaptureService;
